/**
 * The skill tree, bought with Ideas.
 *
 * Effects are declared as typed modifiers and resolved generically in
 * formulas.ts. Nothing in the simulation ever switches on a skill id.
 *
 * The jokes live here, in the descriptions, because this is where the player
 * goes looking. Not in the buttons they press a hundred times.
 */

export type Modifier =
  | { kind: 'tps'; mult: number }
  | { kind: 'cooling'; add: number }
  | { kind: 'ventAmount'; set: number }
  | { kind: 'ventCooldown'; set: number }
  | { kind: 'heatExp'; set: number }
  | { kind: 'clockCap'; set: number }
  | { kind: 'clockExp'; set: number }
  | { kind: 'idleOutput'; mult: number }
  | { kind: 'retain'; add: number }
  | { kind: 'gmGain'; add: number }
  | { kind: 'voidResist'; mult: number }
  | { kind: 'offline'; capSeconds: number; rate: number }
  | { kind: 'unlock'; flag: string };

export type SkillBranch =
  | 'cooling'
  | 'clock'
  | 'output'
  | 'meltdown'
  | 'automation'
  | 'defence';

export interface SkillDef {
  readonly id: string;
  readonly name: string;
  readonly branch: SkillBranch;
  readonly cost: number;
  /** Prerequisite skill id, if any. */
  readonly req?: string;
  /** Stamped, factual. Shown on the node. */
  readonly effect: string;
  /** The joke. Shown under the effect. */
  readonly blurb: string;
  /** One effect, or several. Resolved generically either way. */
  readonly modifier: Modifier | readonly Modifier[];
}

export const BRANCH_NAMES: Record<SkillBranch, string> = {
  cooling: 'Cooling',
  clock: 'Clock',
  output: 'Output',
  meltdown: 'Meltdown',
  automation: 'Automation',
  defence: 'Defence',
};

export const BRANCH_ORDER: readonly SkillBranch[] = [
  'cooling',
  'clock',
  'output',
  'meltdown',
  'automation',
  'defence',
];

export const SKILLS: readonly SkillDef[] = [
  // --- Cooling -------------------------------------------------------------
  {
    id: 'heatsink1',
    name: 'Heatsink',
    branch: 'cooling',
    cost: 5,
    effect: 'Cooling +0.06 / s',
    blurb: 'Aluminium, bolted on with two screws and no thermal interface. It helps.',
    modifier: { kind: 'cooling', add: 0.06 },
  },
  {
    id: 'heatsink2',
    name: 'Copper Heatsink',
    branch: 'cooling',
    cost: 25,
    req: 'heatsink1',
    effect: 'Cooling +0.10 / s',
    blurb: 'Copper conducts heat better than aluminium. It also conducts everything else.',
    modifier: { kind: 'cooling', add: 0.1 },
  },
  {
    id: 'heatsink3',
    name: 'Cryo Loop',
    branch: 'cooling',
    cost: 120,
    req: 'heatsink2',
    effect: 'Cooling +0.15 / s',
    blurb: 'A closed loop of something the datasheet declines to name.',
    modifier: { kind: 'cooling', add: 0.15 },
  },
  {
    id: 'vent_cap',
    name: 'Wide Vent',
    branch: 'cooling',
    cost: 40,
    effect: 'Vent removes 55 heat',
    blurb: 'A larger aperture. The sound it makes is worse, which is unrelated.',
    modifier: { kind: 'ventAmount', set: 55 },
  },
  {
    id: 'vent_cd',
    name: 'Fast Recharge',
    branch: 'cooling',
    cost: 60,
    req: 'vent_cap',
    effect: 'Vent cooldown 45 s to 30 s',
    blurb: 'The reservoir refills faster. Nobody has asked where from.',
    modifier: { kind: 'ventCooldown', set: 30 },
  },
  {
    id: 'paste',
    name: 'Thermal Paste',
    branch: 'cooling',
    cost: 300,
    req: 'heatsink3',
    effect: 'Heat exponent 2.60 to 2.45',
    blurb: 'Fifteen years of research, one grey tube, and a materially flatter curve.',
    modifier: { kind: 'heatExp', set: 2.45 },
  },

  // --- Clock ---------------------------------------------------------------
  {
    id: 'cap1',
    name: 'Unlocked Multiplier',
    branch: 'clock',
    cost: 15,
    effect: 'Clock ceiling 300% to 400%',
    blurb: 'The stop was a small metal tab. It is now in a drawer.',
    modifier: { kind: 'clockCap', set: 400 },
  },
  {
    id: 'cap2',
    name: 'Bus Overvolt',
    branch: 'clock',
    cost: 150,
    req: 'cap1',
    effect: 'Clock ceiling 400% to 550%, cooling +0.25 / s',
    blurb: 'Rated for 400. Rated by cowards. Comes with a bigger radiator, because the last operator asked.',
    // The ceiling alone was not worth 150 ideas: nothing in the tree makes 500%
    // survivable, so the extra range is headroom for a later prestige layer.
    // The cooling is what pays for the purchase today.
    modifier: [
      { kind: 'clockCap', set: 550 },
      { kind: 'cooling', add: 0.25 },
    ],
  },
  {
    id: 'tuned',
    name: 'Tuned Clock',
    branch: 'clock',
    cost: 200,
    req: 'cap1',
    effect: 'Clock exponent 1.50 to 1.62',
    blurb: 'Every point of clock buys more than it did. Heat did not get the memo.',
    modifier: { kind: 'clockExp', set: 1.62 },
  },
  {
    id: 'idle_clock',
    name: 'Efficiency Bias',
    branch: 'clock',
    cost: 30,
    effect: 'At 100% clock or below, output x1.25',
    blurb: 'For operators who intend to sleep. The rig finds this suspicious.',
    modifier: { kind: 'idleOutput', mult: 1.25 },
  },

  // --- Output --------------------------------------------------------------
  {
    id: 'myelin1',
    name: 'Myelin',
    branch: 'output',
    cost: 10,
    effect: 'Output x1.5',
    blurb: 'Insulation. The signal stops leaking into the fluid.',
    modifier: { kind: 'tps', mult: 1.5 },
  },
  {
    id: 'myelin2',
    name: 'Dense Myelin',
    branch: 'output',
    cost: 80,
    req: 'myelin1',
    effect: 'Output x2',
    blurb: 'More insulation. There is no structural upper limit on insulation.',
    modifier: { kind: 'tps', mult: 2 },
  },
  {
    id: 'myelin3',
    name: 'Saltatory Conduction',
    branch: 'output',
    cost: 500,
    req: 'myelin2',
    effect: 'Output x3',
    blurb: 'The signal now skips the boring parts. So does the brain.',
    modifier: { kind: 'tps', mult: 3 },
  },

  // --- Meltdown ------------------------------------------------------------
  {
    id: 'retain',
    name: 'Scar Tissue',
    branch: 'meltdown',
    cost: 100,
    effect: 'Keep 10% of each generator through a meltdown',
    blurb: 'Some structures survive. They are not the same structures afterwards.',
    modifier: { kind: 'retain', add: 0.1 },
  },
  {
    id: 'fry1',
    name: 'Deeper Fry',
    branch: 'meltdown',
    cost: 75,
    effect: 'Idea yield +25%',
    blurb: 'Longer in the oil. Better yield. Established practice.',
    modifier: { kind: 'gmGain', add: 0.25 },
  },
  {
    id: 'fry2',
    name: 'Deepest Fry',
    branch: 'meltdown',
    cost: 400,
    req: 'fry1',
    effect: 'Idea yield +50%',
    blurb: 'There is a depth past which the tissue stops objecting.',
    modifier: { kind: 'gmGain', add: 0.5 },
  },

  // --- Defence -------------------------------------------------------------
  {
    id: 'antibodies',
    name: 'Antibodies',
    branch: 'defence',
    cost: 20,
    effect: 'Hold the void off 60% longer',
    blurb: 'They patrol. They do not ask what they are patrolling against.',
    modifier: { kind: 'voidResist', mult: 1.6 },
  },
  {
    id: 'antibodies2',
    name: 'Adaptive Response',
    branch: 'defence',
    cost: 140,
    req: 'antibodies',
    effect: 'Hold the void off twice as long again',
    blurb: 'It learns the shape of the intrusions. It has started anticipating them.',
    modifier: { kind: 'voidResist', mult: 2 },
  },
  {
    id: 'quiet_mind',
    name: 'Quiet Room',
    branch: 'defence',
    cost: 450,
    req: 'antibodies2',
    effect: 'Hold the void off three times as long again',
    blurb: 'Soundproofed, unlit, and containing exactly one jar.',
    modifier: { kind: 'voidResist', mult: 3 },
  },

  // --- Automation ----------------------------------------------------------
  {
    id: 'autobuy',
    name: 'Purchase Daemon',
    branch: 'automation',
    cost: 50,
    effect: 'Unlocks per-generator auto-buy',
    blurb: 'It spends your thoughts on more thoughts. It does not ask first.',
    modifier: { kind: 'unlock', flag: 'autobuy' },
  },
  {
    id: 'automelt',
    name: 'Failsafe Trigger',
    branch: 'automation',
    cost: 250,
    req: 'autobuy',
    effect: 'Unlocks auto-meltdown at a set temperature',
    blurb: 'Calling it a failsafe was a decision made by the marketing department.',
    modifier: { kind: 'unlock', flag: 'automelt' },
  },
  {
    id: 'cold_storage',
    name: 'Cold Storage',
    branch: 'automation',
    cost: 120,
    effect: 'Offline cap 8 h to 16 h, rate 60% to 80%',
    blurb: 'The rig keeps working while you are away. It notices that you left.',
    modifier: { kind: 'offline', capSeconds: 16 * 60 * 60, rate: 0.8 },
  },
];

export const SKILLS_BY_ID: Record<string, SkillDef> = Object.fromEntries(
  SKILLS.map((s) => [s.id, s]),
);
